"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";
const WIDGET_READY_TIMEOUT_MS = 10000;
const WIDGET_READY_POLL_MS = 100;
const RESPONSE_TIMEOUT_MS = 20000;
const INTERACTIVE_RESPONSE_TIMEOUT_MS = 120000;

interface TurnstileContextValue {
  getToken: () => Promise<string>;
}

type PendingResult = { token: string } | { error: Error };

const TurnstileContext = createContext<TurnstileContextValue | null>(null);

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

export const useTurnstile = () => {
  const ctx = useContext(TurnstileContext);
  if (!ctx)
    throw new Error("useTurnstile must be used within TurnstileProvider");
  return ctx;
};

export const TurnstileProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const isWidgetReadyRef = useRef(false);
  const isMountedRef = useRef(false);
  const inFlightPromiseRef = useRef<Promise<string> | null>(null);
  const resolveTokenRef = useRef<((token: string) => void) | null>(null);
  const rejectTokenRef = useRef<((error: Error) => void) | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isInteractive, setIsInteractive] = useState(false);

  const clearResponseTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const finishPendingRequest = useCallback(
    (result: PendingResult) => {
      const resolveToken = resolveTokenRef.current;
      const rejectToken = rejectTokenRef.current;

      clearResponseTimeout();
      resolveTokenRef.current = null;
      rejectTokenRef.current = null;

      if (isMountedRef.current) {
        setIsVerifying(false);
        setIsInteractive(false);
      }

      if ("token" in result) {
        resolveToken?.(result.token);
      } else {
        rejectToken?.(result.error);
      }
    },
    [clearResponseTimeout],
  );

  const scheduleResponseTimeout = useCallback(
    (timeoutMs: number) => {
      clearResponseTimeout();
      timeoutRef.current = setTimeout(() => {
        finishPendingRequest({
          error: new Error("Security check timed out. Please try again."),
        });
      }, timeoutMs);
    },
    [clearResponseTimeout, finishPendingRequest],
  );

  const waitForWidget = useCallback(async () => {
    const deadline = Date.now() + WIDGET_READY_TIMEOUT_MS;

    while (isMountedRef.current && Date.now() < deadline) {
      if (
        window.turnstile &&
        isWidgetReadyRef.current &&
        containerRef.current &&
        widgetIdRef.current !== null
      ) {
        return true;
      }

      await wait(WIDGET_READY_POLL_MS);
    }

    return false;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    if (!TURNSTILE_SITE_KEY) {
      return () => {
        isMountedRef.current = false;
      };
    }

    const renderWidget = () => {
      if (!containerRef.current || widgetIdRef.current !== null) return;
      if (!window.turnstile) return;

      const widgetId = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        size: "normal",
        execution: "execute",
        appearance: "interaction-only",
        callback: (token) => {
          finishPendingRequest({ token });
        },
        "error-callback": () => {
          finishPendingRequest({
            error: new Error("Verification failed. Please try again."),
          });
        },
        "expired-callback": () => {
          finishPendingRequest({
            error: new Error("Verification expired. Please try again."),
          });
        },
        "timeout-callback": () => {
          finishPendingRequest({
            error: new Error("Security check timed out. Please try again."),
          });
        },
        "before-interactive-callback": () => {
          if (!rejectTokenRef.current) return;

          if (isMountedRef.current) {
            setIsInteractive(true);
          }
          scheduleResponseTimeout(INTERACTIVE_RESPONSE_TIMEOUT_MS);
        },
        "after-interactive-callback": () => {
          if (isMountedRef.current) {
            setIsInteractive(false);
          }
        },
        "unsupported-callback": () => {
          finishPendingRequest({
            error: new Error(
              "Security check is not supported by this browser. Please try another browser.",
            ),
          });
        },
      });

      if (widgetId !== undefined) {
        widgetIdRef.current = widgetId;
        isWidgetReadyRef.current = true;
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      window.onTurnstileLoad = renderWidget;
    }

    return () => {
      isMountedRef.current = false;

      const rejectToken = rejectTokenRef.current;
      clearResponseTimeout();
      resolveTokenRef.current = null;
      rejectTokenRef.current = null;
      rejectToken?.(
        new Error("Security check was interrupted. Please try again."),
      );

      if (window.onTurnstileLoad === renderWidget) {
        window.onTurnstileLoad = undefined;
      }

      isWidgetReadyRef.current = false;
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [clearResponseTimeout, finishPendingRequest, scheduleResponseTimeout]);

  const getToken = useCallback((): Promise<string> => {
    if (!TURNSTILE_SITE_KEY) {
      return Promise.reject(
        new Error("Security check is not configured. Please contact support."),
      );
    }

    if (inFlightPromiseRef.current) {
      return inFlightPromiseRef.current;
    }

    const request = (async () => {
      const isReady = await waitForWidget();

      if (!isReady || !window.turnstile || widgetIdRef.current === null) {
        throw new Error(
          "Security check couldn't load. Please check your connection or disable your ad blocker, then try again.",
        );
      }

      return new Promise<string>((resolve, reject) => {
        const turnstile = window.turnstile;
        const widgetId = widgetIdRef.current;

        if (!turnstile || widgetId === null) {
          reject(
            new Error(
              "Security check couldn't load. Please check your connection or disable your ad blocker, then try again.",
            ),
          );
          return;
        }

        resolveTokenRef.current = resolve;
        rejectTokenRef.current = reject;
        setIsVerifying(true);
        scheduleResponseTimeout(RESPONSE_TIMEOUT_MS);

        try {
          turnstile.reset(widgetId);
          turnstile.execute(widgetId);
        } catch {
          finishPendingRequest({
            error: new Error("Verification failed to start. Please try again."),
          });
        }
      });
    })();

    inFlightPromiseRef.current = request;
    const clearInFlightRequest = () => {
      if (inFlightPromiseRef.current === request) {
        inFlightPromiseRef.current = null;
      }
    };
    request.then(clearInFlightRequest, clearInFlightRequest);

    return request;
  }, [finishPendingRequest, scheduleResponseTimeout, waitForWidget]);

  return (
    <TurnstileContext.Provider value={{ getToken }}>
      {children}
      {isInteractive && (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9998,
            background: "rgba(20, 16, 34, 0.45)",
          }}
        />
      )}
      <div
        ref={containerRef}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          zIndex: 9999,
          width: 300,
          maxWidth: "calc(100vw - 32px)",
          minHeight: 65,
          transform: "translate(-50%, -50%)",
          visibility: isVerifying ? "visible" : "hidden",
          pointerEvents: isVerifying ? "auto" : "none",
        }}
      />
    </TurnstileContext.Provider>
  );
};
